export type TurnstileWidgetOptions = {
  sitekey: string
  execution: 'execute'
  appearance: 'interaction-only'
  action?: string
  callback: (token: string) => void
  'error-callback': (errorCode: string) => void
  'expired-callback': () => void
  'timeout-callback': () => void
}

export type TurnstileGlobal = {
  render: (container: string, options: TurnstileWidgetOptions) => string
  execute: (widgetId: string) => void
  reset: (widgetId: string) => void
  remove: (widgetId: string) => void
}

declare global {
  interface Window {
    turnstile?: TurnstileGlobal
  }
}

export const TURNSTILE_SCRIPT_SRC =
  'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit'

let turnstileScriptPromise: Promise<void> | null = null

export function getTurnstileSiteKey(): string {
  const siteKey = process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY
  if (!siteKey) throw new Error('Turnstile site key is not defined')
  return siteKey
}

export function loadTurnstileScript(): Promise<void> {
  if (typeof window === 'undefined') {
    return Promise.reject(new Error('Turnstile is not available on the server'))
  }

  if (window.turnstile) return Promise.resolve()
  if (turnstileScriptPromise) return turnstileScriptPromise

  turnstileScriptPromise = new Promise<void>((resolve, reject) => {
    const existingScript = document.querySelector<HTMLScriptElement>(
      `script[src="${TURNSTILE_SCRIPT_SRC}"]`
    )

    if (existingScript) {
      existingScript.addEventListener('load', () => resolve(), { once: true })
      existingScript.addEventListener(
        'error',
        () => {
          turnstileScriptPromise = null
          reject(new Error('Unable to load Turnstile'))
        },
        { once: true }
      )
      return
    }

    const script = document.createElement('script')
    script.setAttribute('data-cfasync', 'false')
    script.src = TURNSTILE_SCRIPT_SRC
    script.async = true
    script.defer = true
    script.onload = () => resolve()
    script.onerror = () => {
      turnstileScriptPromise = null
      reject(new Error('Unable to load Turnstile'))
    }

    document.head.appendChild(script)
  })

  return turnstileScriptPromise
}
